import React, { useState } from 'react';

import { AxiosResponse } from 'axios';
import { useNormalizeReferences } from 'hooks/useNormalizeReferences';
import { useGenerateNotificationKey } from 'pages/Merchants/hooks/useGenerateNotificationKey';
import { useGetPlatforms } from 'pages/Merchants/hooks/useGetPlatforms';
import { useNotificationKeyValidation } from 'pages/Merchants/hooks/useNotificationKeyValidation';
import { required } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import {
  AutocompleteInput,
  BooleanInput,
  KeyInputIntrospected,
  AutocompleteMultiselectInput,
  SelectInput,
} from 'shared/react-admin/Inputs';
import {
  TextInputIntrospected,
  TextInputWithAliasIntrospected,
} from 'shared/react-admin/Inputs/TextInputs';
import {
  ReferenceArrayInputIntrospected,
  ReferenceInputIntrospected,
} from 'shared/react-admin/ReferenceInputs';
import { Referral } from 'types/embedded/referral';

import { BoxStyled } from './MerchantsEdit.styled';
import { MerchantsEditConfirmGenerateKeyDialog } from './MerchantsEditConfirmGenerateKeyDialog';
import { MerchantsEditFormSkeleton } from './MerchantsEditFormSkeleton';

export const MerchantsEditForm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { setValue, clearErrors, getValues } = useFormContext();

  const { generateNotificationKey } = useGenerateNotificationKey({
    onSuccess: (data: AxiosResponse) => {
      clearErrors('notificationKey');
      setValue('notificationKey', data.data.value);
    },
  });

  const { notificationKeyValidation } = useNotificationKeyValidation();
  const platforms = useGetPlatforms();

  const { ready } = useNormalizeReferences({
    referenceFields: ['companies', 'referrals', 'platform', 'language'],
  });

  if (!ready) {
    return <MerchantsEditFormSkeleton />;
  }

  return (
    <BoxStyled>
      <TextInputIntrospected
        fullWidth
        label="Название"
        size="small"
        source="name"
        variant="outlined"
      />
      <TextInputWithAliasIntrospected
        fullWidth
        label="Алиас"
        size="small"
        source="alias"
        variant="outlined"
      />
      <KeyInputIntrospected
        fullWidth
        label="Ключ нотификации"
        onClickGenerateKey={() => setIsOpen(true)}
        size="small"
        source="notificationKey"
        validate={[required(), notificationKeyValidation]}
        value={getValues().notificationKey}
        variant="outlined"
      />
      <TextInputIntrospected
        fullWidth
        label="ID канала для missMatch уведомлений"
        size="small"
        source="missmatchChatId"
        variant="outlined"
      />
      <ReferenceInputIntrospected reference="languages" source="language">
        <AutocompleteInput
          label="Язык уведомлений"
          name="language"
          optionText="description"
          size="small"
          validate={required()}
          variant="outlined"
        />
      </ReferenceInputIntrospected>
      <ReferenceArrayInputIntrospected
        filter={{ archive: false }}
        perPage={200}
        reference="companies"
        source="companies"
      >
        <AutocompleteMultiselectInput
          helperText={false}
          label="Компании"
          name="companies"
          optionText="name"
          size="small"
        />
      </ReferenceArrayInputIntrospected>
      <ReferenceArrayInputIntrospected perPage={200} reference="referrals" source="referrals">
        <AutocompleteMultiselectInput
          filterToQuery={(searchText: string) => {
            if (!isNaN(Number(searchText))) {
              return { refId: searchText };
            }
            return { name: searchText };
          }}
          getOptionLabel={(record: Referral) => `${record?.refId}: ${record?.name}`}
          helperText={false}
          label="Рефералы"
          name="referrals"
          size="small"
          sx={{ marginBottom: '24px' }}
        />
      </ReferenceArrayInputIntrospected>
      <SelectInput
        choices={platforms}
        fullWidth
        helperText={false}
        label="Платформа"
        name="platform"
        optionText="label"
        optionValue="@id"
        size="small"
        source="platform"
        sx={{ marginBottom: '24px!important' }}
        validate={required()}
        variant="outlined"
      />
      <BooleanInput
        label="Партнерство"
        size="small"
        source="partner"
        sx={{ marginTop: '8px', marginLeft: '7px' }}
      />
      <MerchantsEditConfirmGenerateKeyDialog
        onClose={() => setIsOpen(false)}
        onConfirm={generateNotificationKey}
        open={isOpen}
      />
    </BoxStyled>
  );
};
